import { Component, OnInit, OnDestroy, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { PaymentService, CreatePaymentRequest } from '../payment.service';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';

declare var MercadoPago: any;

interface Plan {
  id: string;
  name: string;
  price: number;
  currency: string;
  period: string;
  tagline: string;
}

@Component({
  selector: 'app-pricing',
  templateUrl: './pricing.component.html',
  styleUrls: ['./pricing.component.css']
})
export class PricingComponent implements OnInit, OnDestroy, AfterViewInit {
  selectedTab: 'personal' | 'empresa' = 'personal';
  isDarkMode = true;
  private themeObserver?: MutationObserver;
  
  // Estados de pago
  showPaymentModal = false;
  selectedPlan: Plan | null = null;
  isProcessingPayment = false;
  paymentStatus: 'idle' | 'processing' | 'success' | 'error' = 'idle';
  paymentErrorMessage = '';
  paymentSuccessMessage = '';
  
  // Mercado Pago
  private mercadoPago: any = null;
  private cardPaymentBrick: any = null;
  private mpPublicKey = environment.mercadoPagoPublicKey;
  
  // Planes
  plans: Plan[] = [
    {
      id: 'free',
      name: 'Gratis',
      price: 0,
      currency: 'MXN',
      period: 'mes',
      tagline: 'Obtén acceso básico para probar la app y descubrir sus funciones creativas.'
    },
    {
      id: 'plus',
      name: 'Plus',
      price: 365,
      currency: 'MXN',
      period: 'mes',
      tagline: 'Más acceso a funciones nuevas y potentes para aumentar tu productividad y creatividad.'
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 1099,
      currency: 'MXN',
      period: 'mes',
      tagline: 'Integración total para equipos y desarrolladores que buscan automatizar su flujo creativo.'
    },
    {
      id: 'enterprise',
      name: 'Enterprice',
      price: 4979,
      currency: 'MXN',
      period: 'mes',
      tagline: 'Escala tu proyecto con IA generando miles de imágenes directamente desde tu sitio cada mes.'
    }
  ];
  
  constructor(
    private router: Router,
    private paymentService: PaymentService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.detectTheme();
    this.observeThemeChanges();
  }

  ngAfterViewInit(): void {
    // Esperar a que el SDK de Mercado Pago esté cargado
    if (typeof MercadoPago !== 'undefined') {
      this.initializeMercadoPago();
    } else {
      // Intentar inicializar después de un delay
      setTimeout(() => {
        if (typeof MercadoPago !== 'undefined') {
          this.initializeMercadoPago();
        } else {
          console.error('Mercado Pago SDK no está disponible');
        }
      }, 1000);
    }
  }

  ngOnDestroy(): void {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
    if (this.cardPaymentBrick) {
      this.cardPaymentBrick.unmount();
    }
  }

  private detectTheme() {
    this.isDarkMode = document.body.classList.contains('dark-theme');
  }

  private observeThemeChanges() {
    this.themeObserver = new MutationObserver(() => {
      this.detectTheme();
    });
    
    this.themeObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  private initializeMercadoPago() {
    if (!this.mpPublicKey || this.mpPublicKey === 'TU_PUBLIC_KEY_AQUI') {
      console.warn('Mercado Pago Public Key no configurada');
      return;
    }

    try {
      this.mercadoPago = new MercadoPago(this.mpPublicKey, {
        locale: 'es-MX'
      });
    } catch (error) {
      console.error('Error inicializando Mercado Pago:', error);
    }
  }

  selectTab(tab: 'personal' | 'empresa') {
    this.selectedTab = tab;
  }

  goHome() {
    this.router.navigate(['/home']);
  }

  selectPlanById(planId: string) {
    const plan = this.plans.find(p => p.id === planId);
    if (!plan) {
      console.error('Plan no encontrado:', planId);
      return;
    }
    this.selectPlan(plan);
  }

  private selectPlan(plan: Plan) {
    if (plan.price === 0) {
      // Plan gratis - no requiere pago
      alert('¡Ya tienes acceso al plan gratuito!');
      return;
    }

    this.selectedPlan = plan;
    this.showPaymentModal = true;
    this.paymentStatus = 'idle';
    this.paymentErrorMessage = '';
    this.paymentSuccessMessage = '';

    // Forzar detección de cambios y esperar a que el modal se renderice
    this.cdr.detectChanges();
    setTimeout(() => {
      this.renderPaymentBrick();
    }, 300);
  }

  closePaymentModal() {
    this.showPaymentModal = false;
    this.selectedPlan = null;
    if (this.cardPaymentBrick) {
      this.cardPaymentBrick.unmount();
      this.cardPaymentBrick = null;
    }
    this.paymentStatus = 'idle';
    this.paymentErrorMessage = '';
    this.paymentSuccessMessage = '';
  }

  private renderPaymentBrick() {
    if (!this.mercadoPago || !this.selectedPlan) {
      console.error('Mercado Pago no inicializado o plan no seleccionado');
      return;
    }

    // Limpiar el contenedor antes de renderizar
    const container = document.getElementById('card-payment-brick');
    if (container) {
      container.innerHTML = '';
    }

    // Si ya existe un brick, desmontarlo
    if (this.cardPaymentBrick) {
      this.cardPaymentBrick.unmount();
    }

    const bricks = this.mercadoPago.bricks();

    this.cardPaymentBrick = bricks.create('cardPayment', 'card-payment-brick', {
      initialization: {
        amount: this.selectedPlan.price,
        payer: {
          email: '', // Se completará desde el formulario
        }
      },
      customization: {
        paymentMethods: {
          creditCard: 'all',
          debitCard: 'all',
          bankTransfer: ['pix']
        },
        visual: {
          style: {
            theme: this.isDarkMode ? 'dark' : 'default'
          }
        }
      },
      callbacks: {
        onReady: () => {
          console.log('Card Payment Brick ready');
        },
        onSubmit: async (cardFormData: any) => {
          await this.handlePaymentSubmit(cardFormData);
        },
        onError: (error: any) => {
          console.error('Error en Card Payment Brick:', error);
          this.paymentStatus = 'error';
          this.paymentErrorMessage = error.message || 'Error al procesar el pago. Intenta nuevamente.';
        }
      }
    });
  }

  private async handlePaymentSubmit(cardFormData: any) {
    if (!this.selectedPlan) {
      return;
    }

    this.isProcessingPayment = true;
    this.paymentStatus = 'processing';
    this.paymentErrorMessage = '';
    this.paymentSuccessMessage = '';

    try {
      const paymentRequest: CreatePaymentRequest = {
        token: cardFormData.token,
        transactionAmount: this.selectedPlan.price,
        description: `Suscripción ${this.selectedPlan.name} - ${this.selectedPlan.period}`,
        paymentMethodId: cardFormData.payment_method_id,
        installments: cardFormData.installments || 1,
        issuerId: cardFormData.issuer_id || undefined,
        payer: {
          email: cardFormData.payer.email || '',
          identification: cardFormData.payer.identification ? {
            type: cardFormData.payer.identification.type,
            number: cardFormData.payer.identification.number
          } : undefined
        },
        planId: this.selectedPlan.id,
        planName: this.selectedPlan.name
      };

      const response = await firstValueFrom(this.paymentService.createPayment(paymentRequest));

      if (response && response.status === 'approved') {
        this.paymentStatus = 'success';
        this.paymentSuccessMessage = `¡Pago exitoso! Tu suscripción a ${this.selectedPlan.name} está activa.`;
        
        // Cerrar el modal después de 3 segundos
        setTimeout(() => {
          this.closePaymentModal();
          // Opcional: redirigir a una página de éxito o actualizar el estado del usuario
        }, 3000);
      } else {
        this.paymentStatus = 'error';
        this.paymentErrorMessage = response?.error || response?.statusDetail || 'El pago no pudo ser procesado. Intenta nuevamente.';
      }
    } catch (error: any) {
      console.error('Error procesando pago:', error);
      this.paymentStatus = 'error';
      this.paymentErrorMessage = error.error?.error || error.message || 'Error al procesar el pago. Intenta nuevamente.';
    } finally {
      this.isProcessingPayment = false;
    }
  }

  getPlanPrice(planId: string): number {
    const plan = this.plans.find(p => p.id === planId);
    return plan?.price || 0;
  }
}
